import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import xhr from 'xhr';

export default function RegisterPaymentModal({ isOpen, onClose, school, selectedCharges, total, onSuccess }) {
  const toast = useToast();
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    amount: '',
    payment_date: dayjs().format('YYYY-MM-DD'),
    bank_reference: '',
    method: 'transferencia',
    notes: '',
  });

  React.useEffect(() => {
    if (isOpen) {
      setForm({
        amount: total || '',
        payment_date: dayjs().format('YYYY-MM-DD'),
        bank_reference: '',
        method: 'transferencia',
        notes: '',
      });
    }
  }, [isOpen, total]);

  const canSubmit = form.payment_date && form.bank_reference?.trim() && form.amount !== '' && Number(form.amount) >= 0;
  const amountIsLess = form.amount !== '' && Number(form.amount) < total;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSaving(true);
      await xhr.post('/school-usage-payments', {
        data: {
          school: school.id,
          charge_ids: selectedCharges.map((c) => c.id),
          amount: Number(form.amount),
          payment_date: form.payment_date,
          bank_reference: form.bank_reference.trim(),
          method: form.method,
          notes: form.notes || null,
        },
      });

      toast({ title: 'Pago registrado exitosamente', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
      onClose();
      onSuccess?.();
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al registrar el pago';
      toast({ title: 'Error al registrar pago', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Registrar pago</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Box>
              <Text fontWeight="medium" mb={2}>
                Cargos seleccionados ({selectedCharges.length})
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Materia</Th>
                    <Th>Programa</Th>
                    <Th>Fecha</Th>
                    <Th isNumeric>Monto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedCharges.map((c) => {
                    const attrs = c.attributes || c;
                    return (
                      <Tr key={c.id}>
                        <Td maxW="220px">
                          <Tooltip label={attrs.concept} openDelay={300}>
                            <Text noOfLines={1}>{attrs.concept}</Text>
                          </Tooltip>
                        </Td>
                        <Td maxW="200px">
                          <Tooltip label={attrs.program_name || '—'} openDelay={300}>
                            <Text noOfLines={1} color={attrs.program_name ? 'inherit' : 'gray.400'}>
                              {attrs.program_name || '—'}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>{dayjs(attrs.charge_date).format('DD/MM/YYYY')}</Td>
                        <Td isNumeric>${Number(attrs.amount || 0).toLocaleString()}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              <Flex justifyContent="flex-end" mt={2}>
                <Text fontWeight="bold">Total: ${Number(total || 0).toLocaleString()} COP</Text>
              </Flex>
            </Box>

            <Flex gap={4}>
              <FormControl isRequired flex="1">
                <FormLabel>Monto recibido (COP)</FormLabel>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0"
                />
              </FormControl>
              <FormControl isRequired flex="1">
                <FormLabel>Fecha de transferencia</FormLabel>
                <Input
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                />
              </FormControl>
            </Flex>

            <Flex gap={4}>
              <FormControl isRequired flex="1">
                <FormLabel>Referencia bancaria</FormLabel>
                <Input
                  value={form.bank_reference}
                  onChange={(e) => setForm((f) => ({ ...f, bank_reference: e.target.value }))}
                  placeholder="Número de comprobante"
                />
              </FormControl>
              <FormControl flex="1">
                <FormLabel>Método</FormLabel>
                <Select value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
                  <option value="transferencia">Transferencia</option>
                  <option value="consignacion">Consignación</option>
                  <option value="otro">Otro</option>
                </Select>
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel>Notas (opcional)</FormLabel>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </FormControl>

            {amountIsLess && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                El monto recibido es menor al total de los cargos seleccionados.
              </Alert>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="purple" onClick={handleSubmit} isLoading={saving} isDisabled={!canSubmit}>
            Registrar pago
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
